import { Dispatch, SetStateAction, useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { reset, loginWithGoogle } from "@store/slices/authSlice";
import { useNavigate } from "react-router-dom";
import { useNotification } from "@shared/contexts/NotificationContext";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Google's four-color "G", per their sign-in branding guidelines.
// Inlined so the button has no external asset dependency.
const GoogleGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

import SignUpForm from "./SignUpForm";
import SigninForm from "./SigninForm";
import ForgotPasswordForm from "./ForgotPasswordForm";
import MfaChallengeForm from "./MfaChallengeForm";

type ModalMode = "" | "login" | "register" | "forgot-password";

interface Props {
  modalVisible: string;
  setModalVisible: Dispatch<SetStateAction<string>>;
}

const TITLE_ID = "auth-modal-title";

const AuthenticationModal = ({ modalVisible, setModalVisible }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSuccess, showError } = useNotification();

  const { isForgotPassword, isError, isSuccess, message, mfaPending, isLoading } =
    useAppSelector((state) => state.auth);
  const { userId } = useAppSelector((state) => state.user);

  const mode = modalVisible as ModalMode;
  const isOpen =
    mode === "login" || mode === "register" || mode === "forgot-password";

  // An OAuth sign-in lands back on this page with no modal open; if
  // the account has MFA enrolled, the session arrives at AAL1 and the
  // step-up gate sets mfaPending with nothing on screen to answer it.
  // Open the modal so the challenge form (which takes over the body
  // whenever mfaPending is set) can render. Password sign-ins never
  // hit this: their modal is already open.
  useEffect(() => {
    if (mfaPending && !isOpen) setModalVisible("login");
  }, [mfaPending, isOpen, setModalVisible]);

  const handleClose = useCallback(() => {
    setModalVisible("");
  }, [setModalVisible]);

  // Surface a failed dispatch as a toast and clear the flag so it
  // doesn't re-fire on the next render. Split per-flag effects (one
  // each for error / signup-success / forgot-password-success /
  // post-auth redirect) replace the previous mega-effect that ran on
  // every render and cleared every transient flag at once.
  useEffect(() => {
    if (!isError) return;
    showError(message);
    dispatch(reset());
  }, [isError, message, dispatch, showError]);

  useEffect(() => {
    if (!isSuccess) return;
    showSuccess("Please confirm your email.");
    dispatch(reset());
    handleClose();
  }, [isSuccess, dispatch, showSuccess, handleClose]);

  useEffect(() => {
    if (!isForgotPassword) return;
    showSuccess("Please check your email for a password reset link.");
    dispatch(reset());
    handleClose();
  }, [isForgotPassword, dispatch, showSuccess, handleClose]);

  // Post-auth redirect. Honors a deep-link intent recorded before
  // auth (e.g. an org invite link clicked while signed out). Cleared
  // on read so a normal sign-in afterwards goes to /annogram. Only
  // accept same-app relative paths — guards against a future XSS or
  // stray write planting a scheme-qualified URL.
  useEffect(() => {
    if (!userId) return;
    const redirect = sessionStorage.getItem("postAuthRedirect");
    sessionStorage.removeItem("postAuthRedirect");
    const safeRedirect =
      redirect && redirect.startsWith("/") && !redirect.startsWith("//")
        ? redirect
        : null;
    navigate(safeRedirect ?? "/annogram");
  }, [userId, navigate]);

  // Switching forms (login ↔ register ↔ forgot password) should reset
  // any transient flags from the previous form so a stale error or
  // success state doesn't carry into the new view.
  useEffect(() => {
    dispatch(reset());
  }, [modalVisible, dispatch]);

  const toggleMode = () => {
    setModalVisible(mode === "register" ? "login" : "register");
  };

  let body: JSX.Element | null = null;
  let title = "";

  if (mfaPending) {
    // Step-up takes over the modal regardless of which form opened it.
    // The user has authenticated with password but cannot enter the app
    // until they pass the second factor (or redeem a recovery code).
    body = <MfaChallengeForm />;
    title = "Two-factor authentication";
  } else if (mode === "login") {
    body = <SigninForm />;
    title = "Log in";
  } else if (mode === "register") {
    body = <SignUpForm />;
    title = "Create an account";
  } else if (mode === "forgot-password") {
    body = <ForgotPasswordForm />;
    title = "Reset your password";
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="xs"
      aria-labelledby={TITLE_ID}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 2 } }}
    >
      <DialogTitle
        id={TITLE_ID}
        component="h2"
        sx={{
          pr: 6,
          fontSize: { xs: "1.25rem", sm: "1.375rem" },
          fontWeight: 600,
        }}
      >
        {title}
      </DialogTitle>
      <IconButton
        aria-label="Close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (t) => t.palette.text.secondary,
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ pt: 1 }}>
        {body}
        {!mfaPending && (mode === "login" || mode === "register") && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Divider>
              <Typography variant="body2" color="text.secondary">
                or
              </Typography>
            </Divider>
            <Button
              variant="outlined"
              color="inherit"
              fullWidth
              size="large"
              disabled={isLoading}
              startIcon={<GoogleGlyph />}
              onClick={() => dispatch(loginWithGoogle())}
            >
              Continue with Google
            </Button>
          </Stack>
        )}
      </DialogContent>
      {!mfaPending && (
        <>
          <Divider sx={{ mx: 3 }} />
          <DialogActions
            sx={{
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              py: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {mode === "register" ? (
                <>
                  Already have an account?{" "}
                  <Link
                    component="button"
                    type="button"
                    onClick={toggleMode}
                  >
                    Log in
                  </Link>
                </>
              ) : (
                <>
                  Don't have an account?{" "}
                  <Link
                    component="button"
                    type="button"
                    onClick={toggleMode}
                  >
                    Create one
                  </Link>
                </>
              )}
            </Typography>
            {mode !== "forgot-password" && (
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setModalVisible("forgot-password")}
              >
                Forgot your password?
              </Link>
            )}
            {mode === "forgot-password" && (
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setModalVisible("login")}
              >
                Back to log in
              </Link>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default AuthenticationModal;
